<template>
  <div class="full-height-onload">
    <div v-if="loaded" :class="languageCode">
      <!--    Payment summary    -->
      <div class="payment-calculator-summary">
        <div class="top-container">
          <div class="payment-calculator-container">
            <div class="row">
              <div class="col-sm-6">
                <div>
                  <b>{{ vehicleInfo.vehicleName }}</b>
                </div>
              </div>
              <div class="col-sm-6 payment-estimate">
                <div>{{ paymentSummaryContent.subTitle }}</div>
              </div>
              <div class="col-12">
                <hr class="mt-4 mb-0" />
              </div>
            </div>
          </div>
        </div>
        <div id="stickyHeaderOnScroll">
          <div :style="{ height: headerHeight + 'px' }" class="sticky-header-placeholder"></div>
          <div id="stickyHeader" class="bottom-container">
            <div class="payment-calculator-container">
              <div class="row pt-sm-2">
                <div v-show="paymentMethod === this.enums.PaymentMethod.CASH" class="col-sm-auto">
                  <span class="d-inline-block mr-4">
                    <span class="large-text">{{ $n(cash.paymentAmount, 'currencyDollars', languageCode) }}</span>
                  </span>
                </div>
                <div class="col-sm-auto">
                  <span v-if="paymentMethod === this.enums.PaymentMethod.LEASE" class="d-inline-block mr-4">
                    <span class="large-text">{{ $n(lease.paymentAmount, 'currencyDollars', languageCode) }}</span>
                    <span>{{ $t(`message.eCommerceV2.paymentCalculator.${this.paymentPeriod}Period`) }}</span>
                  </span>
                  <template v-else-if="paymentMethod === this.enums.PaymentMethod.FINANCE">
                    <span class="d-inline-block mr-4">
                      <span class="large-text">{{ $n(finance.paymentAmount, 'currencyDollars', languageCode) }}</span>
                      <span>{{ $t(`message.eCommerceV2.paymentCalculator.${this.paymentPeriod}Period`) }}</span>
                    </span>
                    <span>
                      <span class="large-text">{{
                        $n(finance.interestRate, 'percent2FractionDigits', languageCode)
                      }}</span>
                      <span>{{ $t('message.eCommerceV2.paymentCalculator.apr') }}</span>
                    </span>
                  </template>
                </div>
                <div class="col btn-col">
                  <mobialis-button
                    class="mt-4 mt-sm-0 cta-btn"
                    width="w-auto"
                    variant="light"
                    @click="$router.push(paymentSummaryContent.btnPath)"
                    data-cy="headerApplyFinancing"
                    v-av-analytics-cta-interaction="{
                      elementText: paymentSummaryContent.btnLabel,
                      eventActionResult: 'open',
                    }"
                    >{{ paymentSummaryContent.btnLabel }}</mobialis-button
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--    Payment methods    -->
      <div v-if="showPaymentMethods" class="payment-calculator-payment-options">
        <div class="payment-calculator-container">
          <a
            v-for="type in paymentMethods"
            :data-cy="type.value"
            :key="type.value"
            @click="paymentMethod = type.value"
            :class="{ active: type.value === paymentMethod }"
          >
            {{ type.label }}
          </a>
        </div>
      </div>
      <div v-else class="bg-grey">
        <div class="payment-calculator-container py-3">
          <div class="media">
            <img
              width="18"
              height="18"
              class="mr-3 mt-1 mt-sm-0"
              :src="require('@/assets/far_fa-bullhorn@1x.svg')"
              alt=""
            />
            <div class="media-body">
              <p class="small m-0">{{ $t('message.eCommerceV2.paymentCalculator.noFinancingMsg') }}</p>
            </div>
          </div>
        </div>
      </div>
      <!--    Payment options   -->
      <div class="payment-calculator-edit-deal">
        <div class="payment-calculator-container">
          <h3>{{ $t('message.eCommerceV2.paymentCalculator.editDeal') }}</h3>
          <div v-show="paymentMethod !== this.enums.PaymentMethod.CASH">
            <form novalidate @submit.prevent>
              <h4 class="mt-35">{{ $t('message.eCommerceV2.paymentCalculator.termInMonths') }}</h4>
              <mobialis-tab-button
                v-show="paymentMethod === this.enums.PaymentMethod.FINANCE"
                :labelClasses="'mr-2'"
                size="small-mobile"
                name="finance-payment-methods"
                :options="financePaymentTerms"
                :value="initialFinancePaymentTerm"
                @change="changeFinancePaymentTerm($event)"
              ></mobialis-tab-button>
              <mobialis-tab-button
                v-show="paymentMethod === this.enums.PaymentMethod.LEASE"
                :labelClasses="'mr-2'"
                size="small-mobile"
                name="lease-payment-methods"
                :options="leasePaymentTerms"
                :value="initialLeasePaymentTerm"
                @change="changeLeasePaymentTerm($event)"
              ></mobialis-tab-button>
              <h4 class="mt-35">{{ $t('message.eCommerceV2.paymentCalculator.paymentPeriod') }}</h4>
              <mobialis-tab-button
                :labelClasses="'mr-2'"
                size="small-mobile"
                name="payment-term"
                :options="paymentPeriods"
                :value="initialPaymentPeriod"
                @change="changePaymentPeriod($event)"
              ></mobialis-tab-button>
              <div v-show="mileageOptions[0] && paymentMethod === this.enums.PaymentMethod.LEASE">
                <h4 class="mt-35">{{ $t('message.eCommerceV2.paymentCalculator.annualMileage') }}</h4>
                <mobialis-tab-button
                  :labelClasses="'mr-2'"
                  size="small-mobile"
                  name="mileage-option"
                  :options="mileageOptions"
                  v-model="initialMileage"
                  @change="changeMileage($event)"
                ></mobialis-tab-button>
              </div>
              <div class="row mt-35">
                <mobialis-text-input-with-validation
                  class="col-sm-5 mb-4"
                  :mask-type="languageCode === 'fr-CA' ? 'frenchInt' : 'moneyInt'"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  :label="$t('message.eCommerceV2.downPaymentLabel')"
                  :value="initialDownPayment"
                  vid="downPayment"
                  data-cy="downPayment"
                  @input="changeDownPayment($event)"
                ></mobialis-text-input-with-validation>
              </div>
            </form>
          </div>
          <div class="row form-row mt-35">
            <div v-if="paymentMethod !== this.enums.PaymentMethod.CASH" class="col-sm-6 mb-4">
              <CTA
                class="h-100"
                :label="
                  isCreditPlus
                    ? $t('message.eCommerceV2.paymentCalculator.yourCreditScore')
                    : $t('message.eCommerceV2.paymentCalculator.creditRange')
                "
                :ctaStyle="'secondary'"
                @click="toGoCreditFlow"
                v-av-analytics-cta-interaction="{
                  elementText: isCreditPlus
                    ? $t('message.eCommerceV2.paymentCalculator.yourCreditScore')
                    : $t('message.eCommerceV2.paymentCalculator.creditRange'),
                  eventActionResult: 'open',
                }"
                data-cy="creditRange"
              >
                <template v-slot:content>
                  <div
                    class="h4 mt-2 mb-0"
                    v-html-safe="isCreditPlus ? creditVisionScore : $t(`message.credit.grade${creditGrade}`)"
                  ></div>
                </template>
              </CTA>
            </div>
            <div class="col-sm-6 mb-4">
              <CTA
                v-if="netTradeInValue"
                @click="$router.push('trade-in/trade-in-results')"
                class="h-100"
                :label="$t('message.eCommerceV2.paymentCalculator.netTrade')"
                :ctaStyle="'secondary'"
                v-av-analytics-cta-interaction="{
                  elementText: $t('message.eCommerceV2.paymentCalculator.netTrade'),
                  eventActionResult: 'open',
                }"
                data-cy="netTrade"
              >
                <template v-slot:content>
                  <div class="h4 mt-2 mb-0">
                    {{ $n(netTradeInValue, 'currencyDollars', languageCode) }}
                  </div>
                </template>
              </CTA>
              <CTA
                @click="redirectToTradeIn"
                v-else
                class="h-100"
                :ctaStyle="'tertiary'"
                data-cy="netTrade"
                v-av-analytics-cta-interaction="{
                  elementText: $t('message.eCommerceV2.paymentCalculator.tradeIn'),
                  eventActionResult: 'open',
                }"
              >
                <template v-slot:content>
                  <div v-if="tradeInSourceIsCarfax" class="font-weight-bold my-2 position-relative">
                    <span> {{ $t('message.eCommerceV2.paymentCalculator.carfaxTradeIn') }}</span>
                    <CarfaxLogoContainer />
                  </div>
                  <div v-else class="font-weight-bold my-2 position-relative">
                    <span class="icon-inline mr-2 position-relative trade-svg">
                      <svg
                        width="12px"
                        height="12px"
                        viewBox="0 0 12 12"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                      >
                        <g id="Desktop" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                          <g
                            id="eComm-/-03-Calculator-/-02-No-trade"
                            transform="translate(-493.000000, -761.000000)"
                            fill="#19222B"
                          >
                            <g id="Icon-/-System-/-Default-/-far_fa-plus" transform="translate(489.000000, 757.000000)">
                              <path
                                d="M15.5,9 L11,9 L11,4.5 C11,4.22375 10.77625,4 10.5,4 L9.5,4 C9.22375,4 9,4.22375 9,4.5 L9,9 L4.5,9 C4.22375,9 4,9.22375 4,9.5 L4,10.5 C4,10.77625 4.22375,11 4.5,11 L9,11 L9,15.5 C9,15.77625 9.22375,16 9.5,16 L10.5,16 C10.77625,16 11,15.77625 11,15.5 L11,11 L15.5,11 C15.77625,11 16,10.77625 16,10.5 L16,9.5 C16,9.22375 15.77625,9 15.5,9 Z"
                              ></path>
                            </g>
                          </g>
                        </g>
                      </svg>
                    </span>
                    <span>{{ $t('message.eCommerceV2.paymentCalculator.tradeIn') }}</span>
                  </div>
                </template>
              </CTA>
            </div>
          </div>
        </div>
      </div>
      <!--   Deal details   -->
      <div class="deal-details">
        <div class="payment-calculator-container">
          <h3 class="mb-4">{{ $t('message.eCommerceV2.paymentCalculator.dealDetails') }}</h3>
        </div>
        <div class="deal-details-wrapper">
          <div class="payment-calculator-container py-4 p">
            <PaymentCalculatorDetail class="mt-3" :details="listedVehiclePriceDetails"> </PaymentCalculatorDetail>

            <!--  Rebates -->
            <PaymentCalculatorDetail
              class="cyRebates"
              v-show="preTaxRebates || postTaxRebates"
              :heading="$t('message.eCommerceV2.paymentCalculator.rebates')"
              :details="rebateDetails"
            >
            </PaymentCalculatorDetail>

            <!--  Down payment -->
            <PaymentCalculatorDetail
              v-show="downPayment && downPayment != 0 && paymentMethod !== this.enums.PaymentMethod.CASH"
              :details="[
                {
                  name: $t('message.eCommerceV2.paymentCalculator.downPayment'),
                  value: $n(-downPayment, 'currency', languageCode),
                },
              ]"
            >
            </PaymentCalculatorDetail>

            <!--  Freight (B&P only) -->
            <PaymentCalculatorDetail
              v-show="vehicleInfo.freight"
              :details="[
                {
                  name: $t('message.eCommerceV2.paymentCalculator.freight'),
                  value: $n(vehicleInfo.freight, 'currency', languageCode),
                },
              ]"
            >
            </PaymentCalculatorDetail>

            <!--  Trade in -->
            <PaymentCalculatorDetail
              v-show="netTradeInValue"
              :heading="$t('message.eCommerceV2.paymentCalculator.netTrade')"
              :details="tradeInDetails"
            >
            </PaymentCalculatorDetail>

            <!--  Luxury tax -->
            <template v-if="luxuryTax">
              <PaymentCalculatorDetail :details="luxuryTaxDetails"> </PaymentCalculatorDetail>
              <tool-tip id="luxuryTaxTooltip" :title="luxuryTaxTooltipTitle" :body="luxuryTaxTooltipBody"></tool-tip>
            </template>

            <div v-if="paymentMethod === this.enums.PaymentMethod.FINANCE">
              <PaymentDetailsFinance></PaymentDetailsFinance>
            </div>

            <div v-else-if="paymentMethod === this.enums.PaymentMethod.LEASE">
              <PaymentDetailsLease></PaymentDetailsLease>
            </div>

            <div v-else-if="paymentMethod === this.enums.PaymentMethod.CASH">
              <PaymentDetailsCash></PaymentDetailsCash>
            </div>

            <!--Possible fees-->
            <div class="p-small text-muted fees-dropdown border-bottom pb-3">
              <div class="dropdown-heading" @click="showFeeDetails = !showFeeDetails">
                {{ possibleFeesContent.title }}
                <img
                  class="chevron float-right"
                  :class="{ 'is-open': showFeeDetails }"
                  src="@/assets/fa/chevron-down.svg"
                  alt=""
                />
              </div>
              <div v-if="showFeeDetails">
                <ul class="fees-list pl-3 pl-sm-4">
                  <li class="mt-2" v-for="(item, index) in possibleFeesContent.list" :key="index">
                    {{ item.name || item }}
                    <span v-if="item.amount" class="float-right">
                      {{ $n(item.amount, 'currency', languageCode) }}
                    </span>
                  </li>
                </ul>
                <p v-if="possibleFeesContent.msg" class="text-muted small font-italic mb-0">
                  {{ possibleFeesContent.msg }}
                </p>
              </div>
            </div>
            <template v-if="this.disclaimer">
              <p class="white-space-pre-wrap text-muted small font-italic mt-5 mb-4">{{ this.disclaimer }}</p>
            </template>
            <template v-else>
              <p class="text-muted small font-italic mt-5">{{ $t('message.eCommerceV2.paymentCalculator.msg1') }}</p>
              <p class="text-muted small font-italic">{{ $t('message.eCommerceV2.paymentCalculator.msg2') }}</p>
              <p class="text-muted small font-italic mb-4">{{ $t('message.eCommerceV2.paymentCalculator.msg3') }}</p>
            </template>
          </div>
        </div>
      </div>
      <!--   CTAs   -->
      <PaymentCalculatorCTAs></PaymentCalculatorCTAs>
    </div>
    <tool-tip
      v-if="!isEcomV2"
      id="listedVehiclePriceTooltip"
      :title="listedVehiclePriceTooltipTitle"
      :body="listedVehiclePriceTooltipBody"
    ></tool-tip>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import cloneDeep from 'lodash/fp/cloneDeep';
import moment from 'moment';
import { enumMixin } from '@/mixins';
import CTA from '@/components/CTA.vue';
import PaymentCalculatorDetail from '@/components/shared/payment-calculator/PaymentCalculatorDetail.vue';
import PaymentDetailsFinance from '@/components/shared/payment-calculator/PaymentDetailsFinance.vue';
import PaymentDetailsLease from '@/components/shared/payment-calculator/PaymentDetailsLease.vue';
import PaymentDetailsCash from '@/components/shared/payment-calculator/PaymentDetailsCash.vue';
import PaymentCalculatorCTAs from '@/components/shared/payment-calculator/PaymentCalculatorCTAs.vue';
import log from '@/services/log';
import ToolTip from '@/components/ToolTip.vue';
import { AVRProducts } from '@/utils/enums';
import CarfaxLogoContainer from '@/components/shared/trade-in-carfax/CarfaxLogoContainer.vue';
import TradeInSource from '@/utils/enums/tradeInSource';

export default {
  label: 'PaymentCalculator',
  components: {
    CarfaxLogoContainer,
    CTA,
    PaymentCalculatorDetail,
    PaymentDetailsFinance,
    PaymentDetailsLease,
    PaymentDetailsCash,
    ToolTip,
    PaymentCalculatorCTAs,
  },
  mixins: [enumMixin],
  data() {
    return {
      selectedCreditRange: cloneDeep(this.$store.state.paymentCalculator.selectedCreditRange),
      initialFinancePaymentTerm: cloneDeep(this.$store.state.paymentCalculator.financePaymentTerm),
      initialLeasePaymentTerm: cloneDeep(this.$store.state.paymentCalculator.leasePaymentTerm),
      initialPaymentPeriod: cloneDeep(this.$store.state.paymentCalculator.paymentPeriod),
      initialMileage: cloneDeep(this.$store.state.paymentCalculator.mileage),
      initialDownPayment: cloneDeep(this.$store.state.paymentCalculator.downPayment),
      headerHeight: null,
      showFeeDetails: false,
      loaded: false,
      initialLeadUpdated: false,
      luxuryTaxTooltipTitle: this.$t('message.eCommerceV2.paymentCalculator.luxuryTax'),
      luxuryTaxTooltipBody: this.$t('message.eCommerceV2.paymentCalculator.luxuryTaxTooltip'),
      listedVehiclePriceTooltipTitle: this.$t('message.eCommerceV2.paymentCalculator.vehiclePrice'),
      listedVehiclePriceTooltipBody: this.$t('message.eCommerceV2.paymentCalculator.listedVehiclePriceTooltip'),
    };
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      vehicleOfInterestVehicleString: 'vehicle/vehicleString',
      buildAndPriceVehicleName: 'buildAndPrice/vehicleName',
      creditScoreGrade: 'credit/creditScoreGrade',
      regionalCreditScoreGrade: 'credit/regionalCreditScoreGrade',
      creditVisionScore: 'credit/creditVisionScore',
      isCreditPlus: 'credit/hasCreditPlusCreditResults',
      provinceCode: 'business/getIntegrationProvince',
      taxRate: 'paymentCalculator/taxRate',
      getCurrentProduct: 'getCurrentProduct',
    }),
    ...mapState('paymentCalculator', [
      'paymentSummaryData',
      'tradeInValue',
      'outstandingTradeInBalance',
      'creditScoreUsedForPaymentCalculation',
      'creditScoreTypeUsedForPaymentCalculation',
      'cash',
      'finance',
      'lease',
      'financePaymentTerm',
      'leasePaymentTerm',
      'mileage',
      'paymentPeriod',
      'downPayment',
    ]),
    ...mapState({
      disableLuxuryTaxCalculations: state => state.business.info.integrationSettings.disableLuxuryTaxCalculations,
      ecomV2Disclaimers: state => state.business.info.ecomV2Settings.disclaimers,
      buildAndPriceDisclaimers: state =>
        state.business.info.buildAndPriceSettings ? state.business.info.buildAndPriceSettings.disclaimers : null,
      vehicleOfInterest: state => state.vehicle.vehicleInfo,
      buildAndPriceVehicle: state =>
        state.buildAndPrice.vehicleConfig && state.buildAndPrice.vehicleConfig.vehicle
          ? state.buildAndPrice.vehicleConfig.vehicle
          : null,
    }),
    paymentMethod: {
      get() {
        return this.$store.state.paymentCalculator.paymentMethod;
      },
      set(value) {
        this.$store.commit('paymentCalculator/SET_STATE', ['paymentMethod', value]);
      },
    },
    isEcomV2() {
      return this.getCurrentProduct === AVRProducts.ECOMV2;
    },
    isBuildAndPrice() {
      return this.getCurrentProduct === AVRProducts.BUILD_AND_PRICE;
    },
    vehicleInfo() {
      if (this.isEcomV2) {
        return {
          vehicleName: this.vehicleOfInterestVehicleString,
          vehiclePrice: Number(this.vehicleOfInterest.price),
          vehicleYear: Number(this.vehicleOfInterest.year),
          freight: null,
          listedVehiclePrice: Number(this.vehicleOfInterest.price),
          isUsedVehicle: this.$route.query.condition === this.enums.VehicleCondition.USED,
          isNewVehicle: this.$route.query.condition === this.enums.VehicleCondition.NEW,
        };
      }
      return {
        vehicleName: this.buildAndPriceVehicleName,
        vehiclePrice:
          Number(this.buildAndPriceVehicle.vehiclePrice.base.msrp) +
          Number(this.buildAndPriceVehicle.vehiclePrice.optionsMsrp) +
          Number(this.buildAndPriceVehicle.vehiclePrice.base.destination) +
          this.provinceFees,
        vehicleYear: Number(this.buildAndPriceVehicle.year),
        freight: Number(this.buildAndPriceVehicle.vehiclePrice.base.destination),
        listedVehiclePrice:
          Number(this.buildAndPriceVehicle.vehiclePrice.base.msrp) +
          Number(this.buildAndPriceVehicle.vehiclePrice.optionsMsrp) +
          this.provinceFees,
        isUsedVehicle: false,
        isNewVehicle: true,
      };
    },
    // Computed helpers
    creditGrade() {
      return this.creditScoreGrade || this.regionalCreditScoreGrade || this.selectedCreditRange;
    },
    pstRate() {
      return this.paymentSummaryData?.taxBreakDown?.pst !== undefined
        ? parseFloat(this.paymentSummaryData.taxBreakDown.pst)
        : null;
    },
    gstRate() {
      return this.paymentSummaryData?.taxBreakDown?.gst !== undefined
        ? parseFloat(this.paymentSummaryData.taxBreakDown.gst)
        : null;
    },
    // Calculate gst on top of pst if we have the data, pst is at least 15% and the business is in BC
    // This will only be the case for vehicles over $150,000
    isBCPSTGSTStacking() {
      return this.provinceCode === 'BC' && this.pstRate && this.pstRate >= 0.15 && this.gstRate;
    },
    provinceFees() {
      // added to the vehicle price for B&P, not added for ecom
      if (this.provinceCode === this.enums.Province.ONTARIO) {
        return 130;
      }
      if (this.provinceCode === this.enums.Province.ALBERTA) {
        return 126.25;
      }
      return 0;
    },
    outstandingTradeIn() {
      return Number(this.outstandingTradeInBalance);
    },
    paymentsAYear() {
      let paymentsAYear;
      switch (this.paymentPeriod) {
        case this.enums.PaymentPeriod.BIWEEKLY:
          paymentsAYear = 26;
          break;
        case this.enums.PaymentPeriod.WEEKLY:
          paymentsAYear = 52;
          break;
        default:
          paymentsAYear = 12;
      }
      return paymentsAYear;
    },
    paymentPeriods() {
      return [
        {
          value: this.enums.PaymentPeriod.WEEKLY,
          label: this.$t('message.eCommerceV2.paymentCalculator.weekly'),
        },
        {
          value: this.enums.PaymentPeriod.BIWEEKLY,
          label: this.$t('message.eCommerceV2.paymentCalculator.biWeekly'),
        },
        {
          value: this.enums.PaymentPeriod.MONTHLY,
          label: this.$t('message.eCommerceV2.paymentCalculator.monthly'),
        },
      ];
    },
    financeRates() {
      return this.paymentSummaryData && this.paymentSummaryData.finance ? this.paymentSummaryData.finance.rates : null;
    },
    netTradeInValue() {
      return this.tradeInValue - this.outstandingTradeIn;
    },
    leaseTerms() {
      return this.paymentSummaryData.lease ? this.paymentSummaryData.lease.terms : null;
    },
    leaseCurrentTermData() {
      return this.paymentSummaryData.lease && this.paymentSummaryData.lease.termValues && this.leasePaymentTerm
        ? this.paymentSummaryData.lease.termValues[`${this.leasePaymentTerm}`]
        : null;
    },
    // ---------- User options
    financePaymentTerms() {
      const terms = [];
      if (this.financeRates) {
        Object.entries(this.financeRates).forEach(entry => {
          const [key] = entry;
          terms.push({
            value: Number(key),
            label: key,
          });
        });
      }
      return terms;
    },
    leasePaymentTerms() {
      const terms = [];
      if (this.leaseTerms) {
        this.leaseTerms.forEach(item => {
          terms.push({
            value: Number(item),
            label: item,
          });
        });
      }
      return terms;
    },
    // Might not include lease, data might not include cash but include in anyways
    paymentMethods() {
      const methods = [];
      const possibleMethods = [
        this.enums.PaymentMethod.FINANCE,
        this.enums.PaymentMethod.LEASE,
        this.enums.PaymentMethod.CASH,
      ];
      possibleMethods.forEach(item => {
        if (this.paymentSummaryData[`${item}`] || item === this.enums.PaymentMethod.CASH) {
          methods.push({
            value: item,
            label: this.$t(`message.eCommerceV2.paymentCalculator.${item}`),
          });
        }
      });
      return methods;
    },
    // Only show payment methods when there are more than 1
    showPaymentMethods() {
      return this.paymentMethods.length > 1;
    },
    // Only possible for lease
    mileageOptions() {
      const options = [];
      if (this.leaseCurrentTermData && this.leaseCurrentTermData.mileageResiduals) {
        Object.entries(this.leaseCurrentTermData.mileageResiduals).forEach(entry => {
          const [key] = entry;
          options.push({
            value: key,
            label: this.$n(key, 'number', this.languageCode),
          });
        });
      }
      return options;
    },
    defaultMileage() {
      return this.paymentSummaryData.lease && this.paymentSummaryData.lease.defaultMileage
        ? this.paymentSummaryData.lease.defaultMileage
        : null;
    },
    // ---------- Stuff for the UI
    // Current rebates
    preTaxRebates() {
      return this[`${this.paymentMethod}`].preTaxRebates || null;
    },
    postTaxRebates() {
      return this[`${this.paymentMethod}`].postTaxRebates || null;
    },
    totalRebates() {
      return this[`${this.paymentMethod}`].preTaxRebatesTotal + this[`${this.paymentMethod}`].postTaxRebatesTotal;
    },
    luxuryTax() {
      return this[`${this.paymentMethod}`].luxuryTax || null;
    },
    rebateDetails() {
      const details = [];
      let rebates = [];
      if (this.preTaxRebates) {
        rebates = [...rebates, ...this.preTaxRebates];
      }
      if (this.postTaxRebates) {
        rebates = [...rebates, ...this.postTaxRebates];
      }
      if (rebates.length > 0) {
        rebates.forEach(item => {
          const isValidExpiryDate = moment(item.expiry, 'YYYY-MM-DD', true).isValid();
          details.push({
            name:
              item.type === 'percentage'
                ? `${item.name} (${item.discount}${this.$t('message.eCommerceV2.paymentCalculator.offMSRP')})`
                : item.name,
            value: this.$n(-item.discountAmount, 'currency', this.languageCode),
            msg: isValidExpiryDate
              ? `${this.$t('message.eCommerceV2.paymentCalculator.expires')} ${moment(item.expiry).format('LL')}`
              : null,
          });
        });
        details.push({
          name: this.$t('message.eCommerceV2.paymentCalculator.subtotal'),
          value: this.$n(-this.totalRebates, 'currency', this.languageCode),
        });
      }
      return details;
    },
    listedVehiclePriceDetails() {
      return this.isEcomV2
        ? [
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.vehiclePrice'),
              value: this.$n(this.vehicleInfo.listedVehiclePrice, 'currency', this.languageCode),
            },
          ]
        : [
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.vehiclePrice'),
              value: this.$n(this.vehicleInfo.listedVehiclePrice, 'currency', this.languageCode),
              tooltipIcon: ['far', 'info-circle'],
              tooltipId: 'listedVehiclePriceTooltip',
            },
          ];
    },
    tradeInDetails() {
      return [
        {
          name: this.$t('message.eCommerceV2.paymentCalculator.tradeInValue'),
          value: this.$n(-this.tradeInValue, 'currency', this.languageCode),
        },
        {
          name: this.$t('message.eCommerceV2.paymentCalculator.outstandingBalance'),
          value: this.$n(this.outstandingTradeInBalance, 'currency', this.languageCode),
        },
        {
          name: this.$t('message.eCommerceV2.paymentCalculator.subtotal'),
          value: this.$n(-this.netTradeInValue, 'currency', this.languageCode),
        },
      ];
    },
    luxuryTaxDetails() {
      return [
        {
          name: this.$t('message.eCommerceV2.paymentCalculator.luxuryTax'),
          value: this.$n(this.luxuryTax, 'currency', this.languageCode),
          tooltipIcon: ['far', 'info-circle'],
          tooltipId: 'luxuryTaxTooltip',
        },
      ];
    },
    paymentSummaryContent() {
      const subTitle = this.$t('message.eCommerceV2.paymentCalculator.paymentEstimate');
      let btnLabel = this.$t('message.eCommerceV2.paymentCalculator.applyForFinancing');
      let btnPath = 'finance/finance-intro';
      if (this.isEcomV2) {
        if (this.paymentMethod === this.enums.PaymentMethod.CASH) {
          btnLabel = this.$t('message.eCommerceV2.paymentCalculator.makeOffer');
          btnPath = 'offer/offer-summary-form';
        }
      }
      return {
        subTitle,
        btnLabel,
        btnPath,
      };
    },
    provincialFeesList() {
      // Only for Alberta and Ontario
      if (this.provinceCode === this.enums.Province.ONTARIO || this.provinceCode === this.enums.Province.ALBERTA) {
        let list = [];
        if (this.provinceCode === this.enums.Province.ONTARIO) {
          list = [
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.OMVICfee'),
              value: 'OMVICfee',
              amount: 12.5,
            },
          ];
        } else {
          list = [
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.AMVICfee'),
              value: 'AMVICfee',
              amount: 10.0,
            },
          ];
        }
        // Add additional fees if it's not a used vehicle
        if (!this.vehicleInfo.isUsedVehicle) {
          list.push(
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.airTax'),
              value: 'airTax',
              amount: 100,
            },
            {
              name: this.$t('message.eCommerceV2.paymentCalculator.tireTax'),
              value: 'tireTax',
              amount: 20,
            }
          );
        }
        return list;
      }
      return null;
    },
    possibleFeesContent() {
      let content = {};
      // Custom fees for ontario and alberta
      if (this.provincialFeesList) {
        content.title = this.$t('message.eCommerceV2.paymentCalculator.levies');
        content.list = this.provincialFeesList;
      } else if (this.provinceCode === this.enums.Province.MANITOBA && this.isEcomV2) {
        content = {
          title: this.$t('message.eCommerceV2.paymentCalculator.possibleFeesManitoba'),
          list: this.$t('message.eCommerceV2.paymentCalculator.possibleFeesListManitoba'),
          msg: this.$t('message.eCommerceV2.paymentCalculator.seeDealer'),
        };
      } else if (this.isEcomV2) {
        content = {
          title: this.$t('message.eCommerceV2.paymentCalculator.possibleFees'),
          list: this.$t('message.eCommerceV2.paymentCalculator.possibleFeesList'),
          msg: this.$t('message.eCommerceV2.paymentCalculator.seeDealer'),
        };
      } else {
        content = {
          title: this.$t('message.eCommerceV2.paymentCalculator.possibleFees'),
          list: this.$t('message.eCommerceV2.paymentCalculator.possibleFeesListBuildAndPrice'),
          msg: this.$t('message.eCommerceV2.paymentCalculator.seeDealer'),
        };
      }
      return content;
    },
    disclaimer() {
      const lang = this.languageCode === 'fr-CA' ? 'fr' : 'en';
      const condition = this.vehicleInfo.isNewVehicle ? 'new' : 'used';
      if (this.isEcomV2 && this.ecomV2Disclaimers) {
        return this.ecomV2Disclaimers[`${condition}`][`${lang}`];
      }
      if (this.buildAndPriceDisclaimers) {
        return this.buildAndPriceDisclaimers[`${lang}`];
      }
      return null;
    },
    tradeInSourceIsCarfax() {
      return this.$store.getters['business/getTradeInSource'] === TradeInSource.CARFAX;
    },
  },
  methods: {
    // When the user changes options
    changeFinancePaymentTerm(val) {
      this.$store.commit('paymentCalculator/SET_STATE', ['financePaymentTerm', val]);
      this.calculateFinancePayment();
    },
    changeLeasePaymentTerm(val) {
      this.$store.commit('paymentCalculator/SET_STATE', ['leasePaymentTerm', val]);
      this.calculateLeasePayment();
    },
    changePaymentPeriod(val) {
      this.$store.commit('paymentCalculator/SET_STATE', ['paymentPeriod', val]);
      this.$store.commit('paymentCalculator/SET_STATE', ['paymentsAYear', this.paymentsAYear]);
      if (this.paymentSummaryData.finance) {
        this.calculateFinancePayment();
      }
      if (this.paymentSummaryData.lease) {
        this.calculateLeasePayment();
      }
    },
    changeMileage(val) {
      this.$store.commit('paymentCalculator/SET_STATE', ['mileage', val]);
      this.calculateLeasePayment();
    },
    changeDownPayment(val) {
      if (this.downPayment !== val) {
        this.$store.commit('paymentCalculator/SET_STATE', ['downPayment', val]);
        if (this.paymentSummaryData.finance) {
          this.calculateFinancePayment();
        }
        if (this.paymentSummaryData.lease) {
          this.calculateLeasePayment();
        }
      }
    },
    setInitialPaymentMethod() {
      if (
        this.paymentMethod === this.enums.PaymentMethod.LEASE &&
        !this.paymentSummaryData.lease &&
        this.paymentSummaryData.finance
      ) {
        this.$store.commit('paymentCalculator/SET_STATE', ['paymentMethod', this.enums.PaymentMethod.FINANCE]);
      } else if (
        this.paymentMethod === this.enums.PaymentMethod.FINANCE &&
        !this.paymentSummaryData.finance &&
        this.paymentSummaryData.lease
      ) {
        this.$store.commit('paymentCalculator/SET_STATE', ['paymentMethod', this.enums.PaymentMethod.LEASE]);
      } else if (
        this.paymentMethod !== this.enums.PaymentMethod.CASH &&
        !this.paymentSummaryData.finance &&
        !this.paymentSummaryData.lease
      ) {
        this.$store.commit('paymentCalculator/SET_STATE', ['paymentMethod', this.enums.PaymentMethod.CASH]);
      }
    },
    // Set initial selections of options
    setDefaultSelectionsAndCalculate() {
      // set static vehicle data to state
      this.$store.commit('paymentCalculator/SET_STATE', ['vehiclePrice', this.vehicleInfo.listedVehiclePrice]);
      // We only add the provincial fees and freight to calculations for B&P
      if (!this.isEcomV2) {
        this.$store.commit('paymentCalculator/SET_STATE', ['freight', this.vehicleInfo.freight]);
        this.$store.commit('paymentCalculator/SET_STATE', ['provincialFees', this.provincialFeesList]);
      }

      // Change selected payment method if we don't have available data
      this.setInitialPaymentMethod();

      // If we have finance as an option
      if (this.paymentSummaryData.finance) {
        // Set default of highest term if value doesn't exist in options
        if (this.financePaymentTerms.filter(term => term.value === this.initialFinancePaymentTerm).length === 0) {
          const lastPosition = this.financePaymentTerms.length - 1;
          this.initialFinancePaymentTerm = this.financePaymentTerms[`${lastPosition}`].value;
          this.$store.commit('paymentCalculator/SET_STATE', ['financePaymentTerm', this.initialFinancePaymentTerm]);
        }
        this.calculateFinancePayment();
      }

      // If we have lease as an option
      if (this.paymentSummaryData.lease) {
        // Set default of highest term if value doesn't exist in options
        if (this.leasePaymentTerms.filter(term => term.value === this.initialLeasePaymentTerm).length === 0) {
          const lastPosition = this.leasePaymentTerms.length - 1;
          this.initialLeasePaymentTerm = this.leasePaymentTerms[`${lastPosition}`].value;
          this.$store.commit('paymentCalculator/SET_STATE', ['leasePaymentTerm', this.initialLeasePaymentTerm]);
        }
        // Set default mileage if option set not available
        if (this.mileageOptions.filter(term => term.value === this.initialMileage).length === 0) {
          this.initialMileage = this.defaultMileage;
          this.$store.commit('paymentCalculator/SET_STATE', ['mileage', this.initialMileage]);
        }
        this.calculateLeasePayment();
      }

      // We will always have cash - no defaults required
      this.calculateCashPayment();
    },
    calculateCashPayment() {
      // Rebates
      const cashPreTaxRebates =
        this.paymentSummaryData?.cash?.incentives?.preTax !== undefined
          ? this.paymentSummaryData.cash.incentives.preTax
          : null;
      const cashPreTaxRebatesData = this.getRebatesData(cashPreTaxRebates);
      const cashPreTaxRebatesTotal = cashPreTaxRebatesData.total;
      const cashPostTaxRebates =
        this.paymentSummaryData?.cash?.incentives?.postTax !== undefined
          ? this.paymentSummaryData.cash.incentives.postTax
          : null;
      const cashPostTaxRebatesData = this.getRebatesData(cashPostTaxRebates);
      const cashPostTaxRebatesTotal = cashPostTaxRebatesData.total;
      const cashLuxuryTax = this.getLuxuryTaxAmount(cashPreTaxRebatesTotal);

      // Calculate payment
      const cashPreTaxVehiclePrice =
        this.vehicleInfo.vehiclePrice - this.tradeInValue - cashPreTaxRebatesTotal + cashLuxuryTax;
      const cashTaxAmount = this.getTaxAmount(cashPreTaxVehiclePrice);

      const cashTotalVehiclePriceWithTax = cashPreTaxVehiclePrice + cashTaxAmount;
      const cashTotalVehiclePricePostTaxRebates =
        cashTotalVehiclePriceWithTax - cashPostTaxRebatesTotal + this.outstandingTradeIn;

      // Update state
      this.$store.commit('paymentCalculator/SET_CASH_DATA', {
        preTaxRebates: cashPreTaxRebatesData.rebates,
        preTaxRebatesTotal: cashPreTaxRebatesTotal,
        postTaxRebates: cashPostTaxRebatesData.rebates,
        postTaxRebatesTotal: cashPostTaxRebatesTotal,
        paymentAmount: cashTotalVehiclePricePostTaxRebates,
        salesTax: cashTaxAmount,
        luxuryTax: cashLuxuryTax,
      });

      // Update lead
      if (this.paymentMethod === this.enums.PaymentMethod.CASH) {
        this.updateLead();
      }
    },
    calculateFinancePayment() {
      // Get rebates
      const financePreTaxRebates =
        this.paymentSummaryData?.finance?.incentives?.preTax !== undefined
          ? this.paymentSummaryData.finance.incentives.preTax
          : null;
      const financePreTaxRebatesData = this.getRebatesData(financePreTaxRebates);
      const financePreTaxRebatesTotal = financePreTaxRebatesData.total;
      const financePostTaxRebates =
        this.paymentSummaryData?.finance?.incentives?.postTax !== undefined
          ? this.paymentSummaryData.finance.incentives.postTax
          : null;
      const financePostTaxRebatesData = this.getRebatesData(financePostTaxRebates);
      const financePostTaxRebatesTotal = financePostTaxRebatesData.total;
      const financeLuxuryTax = this.getLuxuryTaxAmount(financePreTaxRebatesTotal);

      // Calculate payment
      const financePreTaxVehiclePrice =
        this.vehicleInfo.vehiclePrice - this.tradeInValue - financePreTaxRebatesTotal + financeLuxuryTax;
      const financeTaxAmount = this.getTaxAmount(financePreTaxVehiclePrice);

      const financeTotalVehiclePriceWithTax = financePreTaxVehiclePrice + financeTaxAmount;
      const financeNumberOfTotalPayments = this.paymentsAYear * (this.financePaymentTerm / 12);
      const rate = Number(this.financeRates[`${this.financePaymentTerm}`]);
      // Amount financed
      const financeTotalVehiclePricePostTaxRebates =
        financeTotalVehiclePriceWithTax -
        financePostTaxRebatesTotal -
        Number(this.downPayment) +
        this.outstandingTradeIn;
      const interestRatePerPayment = rate / this.paymentsAYear;
      let paymentAmount = null;
      if (interestRatePerPayment === 0) {
        paymentAmount = financeTotalVehiclePricePostTaxRebates / financeNumberOfTotalPayments;
      } else {
        paymentAmount =
          (financeTotalVehiclePricePostTaxRebates /
            (1 - 1 / (1 + interestRatePerPayment) ** financeNumberOfTotalPayments)) *
          interestRatePerPayment;
      }
      const totalLoanAmount = paymentAmount * financeNumberOfTotalPayments;
      const costOfBorrowing = totalLoanAmount - financeTotalVehiclePricePostTaxRebates;

      // Update state
      this.$store.commit('paymentCalculator/SET_FINANCE_DATA', {
        preTaxRebates: financePreTaxRebatesData.rebates,
        postTaxRebates: financePostTaxRebatesData.rebates,
        preTaxRebatesTotal: financePreTaxRebatesTotal,
        postTaxRebatesTotal: financePostTaxRebatesTotal,
        interestRate: rate,
        amountFinanced: financeTotalVehiclePricePostTaxRebates,
        paymentAmount,
        loanAmount: totalLoanAmount,
        salesTax: financeTaxAmount,
        costOfBorrowing,
        luxuryTax: financeLuxuryTax,
      });

      // Update lead
      if (this.paymentMethod === this.enums.PaymentMethod.FINANCE) {
        this.updateLead();
      }
    },
    calculateLeasePayment() {
      // Rebates
      const leasePreTaxRebates =
        this.leaseCurrentTermData?.incentives?.preTax !== undefined
          ? this.leaseCurrentTermData.incentives.preTax
          : null;
      const leasePreTaxRebatesData = this.getRebatesData(leasePreTaxRebates);
      const leasePreTaxRebatesTotal = leasePreTaxRebatesData.total;
      const leasePostTaxRebates =
        this.leaseCurrentTermData?.incentives?.postTax !== undefined
          ? this.leaseCurrentTermData.incentives.postTax
          : null;
      const leasePostTaxRebatesData = this.getRebatesData(leasePostTaxRebates);
      const leasePostTaxRebatesTotal = leasePostTaxRebatesData.total;
      const leaseLuxuryTax = this.getLuxuryTaxAmount(leasePreTaxRebatesTotal);

      // Calculations
      const leaseNumberOfTotalPayments = this.paymentsAYear * (this.leasePaymentTerm / 12);
      // Remove down payment before taxes
      const leasePreTaxVehiclePrice =
        this.vehicleInfo.vehiclePrice -
        this.netTradeInValue -
        leasePreTaxRebatesTotal -
        Number(this.downPayment) +
        leaseLuxuryTax;
      const residualValuePercentage = Number(this.leaseCurrentTermData.mileageResiduals[`${this.mileage}`]);
      const residualValue = residualValuePercentage * this.vehicleInfo.vehiclePrice;
      const moneyFactor = Number(this.leaseCurrentTermData.moneyFactor);
      const depreciationAmount = leasePreTaxVehiclePrice - residualValue;
      const baseLeasePricePerPayment = depreciationAmount / leaseNumberOfTotalPayments;
      const rentChargePerYear = (leasePreTaxVehiclePrice + residualValue) * moneyFactor * 12;
      const rentChargePerPayment = rentChargePerYear / this.paymentsAYear;
      const leasePreTaxPayment = rentChargePerPayment + baseLeasePricePerPayment;
      const leasePreTaxAmount = leasePreTaxPayment * leaseNumberOfTotalPayments;
      const taxAmount = this.getTaxAmount(leasePreTaxAmount);
      const leasePostTaxAmount = taxAmount + leasePreTaxAmount;
      // Amount to be leased
      const leasePostTaxRebatesAmount = leasePostTaxAmount - leasePostTaxRebatesTotal;
      const leasePricePerPaymentPeriod = leasePostTaxRebatesAmount / leaseNumberOfTotalPayments;
      const leaseDownPaymentTax = this.downPayment ? parseFloat(this.downPayment) * this.taxRate : null;
      const dueOnDelivery = this.downPayment
        ? leasePricePerPaymentPeriod + parseFloat(this.downPayment) + leaseDownPaymentTax
        : leasePricePerPaymentPeriod;

      // Update state
      this.$store.commit('paymentCalculator/SET_LEASE_DATA', {
        preTaxVehiclePrice: leasePreTaxVehiclePrice,
        moneyFactor,
        residual: residualValuePercentage,
        residualValue,
        salesTax: taxAmount,
        leaseAmount: leasePostTaxRebatesAmount,
        paymentAmount: leasePricePerPaymentPeriod,
        downPaymentTax: leaseDownPaymentTax,
        dueOnDelivery,
        preTaxRebates: leasePreTaxRebatesData.rebates,
        postTaxRebates: leasePostTaxRebatesData.rebates,
        preTaxRebatesTotal: leasePreTaxRebatesTotal,
        postTaxRebatesTotal: leasePostTaxRebatesTotal,
        luxuryTax: leaseLuxuryTax,
      });

      // Update lead
      if (this.paymentMethod === this.enums.PaymentMethod.LEASE) {
        this.updateLead();
      }
    },
    getRebatesData(rebates) {
      let total = 0;
      if (rebates) {
        rebates.forEach(item => {
          if (item.type === 'percentage') {
            // eslint-disable-next-line no-param-reassign
            item.discountAmount = (item.discount / 100) * this.vehicleInfo.vehiclePrice;
          } else {
            // eslint-disable-next-line no-param-reassign
            item.discountAmount = item.discount;
          }
          total += item.discountAmount;
        });
      }
      return { total, rebates };
    },
    getTaxAmount(price) {
      if (this.isBCPSTGSTStacking) {
        // Calculate the GST on top of the PST
        return price * (1 + this.pstRate) * (1 + this.gstRate) - price;
      }
      return price * this.taxRate;
    },
    getLuxuryTaxAmount(preTaxRebatesTotal) {
      if (
        (this.disableLuxuryTaxCalculations !== true || !this.isEcomV2) && // Can disable for ecom only
        this.vehicleInfo.vehiclePrice - preTaxRebatesTotal >= 100000 &&
        this.vehicleInfo.isNewVehicle &&
        this.vehicleInfo.vehicleYear >= 2018
      ) {
        const res = (this.vehicleInfo.vehiclePrice - preTaxRebatesTotal - 100000) * 0.2;
        const res2 = (this.vehicleInfo.vehiclePrice - preTaxRebatesTotal) * 0.1;
        if (res2 < res) {
          return res2;
        }
        return res;
      }
      return 0;
    },
    handleScroll() {
      if (this.isEcomV2) {
        const header = document.getElementById('stickyHeaderOnScroll');
        if (header?.classList && window.pageYOffset > header.offsetTop - 45) {
          header.classList.add('fixed-on-scroll');
        } else if (header?.classList) {
          header.classList.remove('fixed-on-scroll');
        }
        if (header?.classList && window.pageYOffset > header.offsetTop - 45 + this.headerHeight) {
          header.classList.add('shrink');
        } else if (header?.classList) {
          header.classList.remove('shrink');
        }
      }
    },
    toGoCreditFlow() {
      // If we already have a credit score just go to the results page
      // User is not allowed to change credit score once they have one
      if (this.creditScoreGrade || this.regionalCreditScoreGrade) {
        this.$router.push('credit/credit-results');
      } else {
        this.$router.push('credit/select-credit-range');
      }
    },
    // Call update lead on load after initial payment has been calculated
    async updateLead() {
      if (!this.initialLeadUpdated && this.$store.getters['lead/getLeadId'] !== '') {
        try {
          await this.$store.dispatch('lead/updateLead');
        } catch (err) {
          // Just going to log this for now, not sure if error page is necessary
          log('Error in update lead payment calculator', err);
        }
      }
      this.initialLeadUpdated = true;
    },
    // @TODO - remove this route when VRM removed
    redirectToTradeIn() {
      // eslint-disable-next-line no-unused-expressions
      this.tradeInSourceIsCarfax
        ? this.$router.push('trade-in/carfax-basic-form')
        : this.$router.push('trade-in/vehicle-autocomplete');
    },
  },
  async mounted() {
    window.addEventListener('scroll', this.handleScroll);
    // Get header height for fixed header on scroll styling
    this.$nextTick(() => {
      const header = document.getElementById('stickyHeader');
      this.headerHeight = header ? header.offsetHeight : 0;
    });
  },
  async created() {
    moment.locale(this.languageCode);
    this.$store.commit('SET_LOADING', true);
    // Get payment data if we don't have it or credit type or credit score has changed
    let availableCreditType = this.enums.CreditType.USER_SUPPLIED;
    if (this.creditScoreGrade) {
      availableCreditType = this.enums.CreditType.TRANSUNION_PERSONALIZED;
    } else if (this.regionalCreditScoreGrade) {
      availableCreditType = this.enums.CreditType.EQUIFAX_REGIONAL;
    }
    if (
      !this.paymentSummaryData ||
      this.creditScoreTypeUsedForPaymentCalculation !== availableCreditType ||
      this.creditScoreUsedForPaymentCalculation !== this.creditGrade ||
      (this.isBuildAndPrice &&
        this.$store.state.buildAndPrice.vehicleConfig.serializedValue !==
          this.$store.state.paymentCalculator.buildAndPriceVehicleUsedForPaymentCalculation) // if we have a new vehicle need to get payments data again
    ) {
      try {
        await this.$store.dispatch('paymentCalculator/getPaymentCalculatorSettings', {
          isEcomV2: this.isEcomV2,
          vehiclePrice: this.vehicleInfo.vehiclePrice,
        });
        // Two sample data versions, most and least data examples
        // const dummyData = require('../../../mock/paymentCalculator.json');
        // this.$store.commit('paymentCalculator/SET_STATE', ['paymentSummaryData', dummyData]);
        this.setDefaultSelectionsAndCalculate();
        this.loaded = true;
        this.$nextTick(() => {
          const header = document.getElementById('stickyHeader');
          this.headerHeight = header ? header.offsetHeight : 0;
        });
      } catch (error) {
        log('Error in ecomv2 getV2PaymentCalculatorSettings', error);
        this.$router.push('error');
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    } else {
      this.setDefaultSelectionsAndCalculate();
      this.loaded = true;
      this.$store.commit('SET_LOADING', false);
    }
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>
<style lang="scss">
.payment-calculator-edit-deal {
  .carfax-container {
    background-color: transparent;
    span {
      font-size: 12px;
      color: $primary-grey;
    }
  }
}
</style>
<style scoped lang="scss" src="@/assets/scss/_payment-calculator.scss"></style>
